import { useEffect, useState, useCallback } from "react";
import { Container, Typography, Stack, Box, TextField, InputAdornment, ToggleButton, ToggleButtonGroup, Button } from "@mui/material";
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getPosts, Post } from "../api/posts";
import { getMyVotes } from "../api/votes";
import { getTopics, Topic } from "../api/topics";
import { getMyProfile } from "../api/profile";

import PostCard from "../components/PostCard";
import CreatePostDialog from "../components/CreatePostDialog";
import TopicSidebar from "../components/TopicSidebar";
import CreatePostBar from "../components/CreatePostBar";
import CommunityInfo from "../components/CommunityInfo";
import CreateTopicDialog from "../components/CreateTopicDialog";

export default function FeedPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [open, setOpen] = useState(false);
  const [openTopic, setOpenTopic] = useState(false);

  const [myVotes, setMyVotes] = useState<Record<number, number>>({});
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  // New state
  const [sortBy, setSortBy] = useState<string>("top");
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch posts + (if logged in) my votes
  const refresh = useCallback(
    async (topicId?: number | null) => {
      const effectiveTopicId = topicId ?? selectedTopicId;
      const data = await getPosts(effectiveTopicId ?? undefined, sortBy, searchQuery);
      setPosts(data);

      const token = localStorage.getItem("token");
      if (token) {
        try {
            const raw = await getMyVotes(); 
            const mapped: Record<number, number> = {};
            for (const [k, v] of Object.entries(raw)) mapped[Number(k)] = v;
            setMyVotes(mapped);
        } catch (e) {
            console.error("Failed to fetch votes", e);
        }
      } else {
        setMyVotes({});
      }
    },
    [selectedTopicId, sortBy, searchQuery]
  );

  const refreshTopics = useCallback(async () => {
      try {
        const t = await getTopics();
        setTopics(t);
      } catch {}
  }, []);

  // Initial load: topics + posts + profile
  useEffect(() => {
    refreshTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refresh(null); 
    if (localStorage.getItem("token")) {
        getMyProfile().then(p => setCurrentUserId(p.user_id)).catch(() => {});
    }
  }, [refreshTopics]); // Removed refresh from deps to avoid loop if not handled carefully, but refresh is stable via useCallback

  // Handle URL topic param
  useEffect(() => {
      const topicName = searchParams.get("topic");
      if (topicName && topics.length > 0) {
          const t = topics.find(t => t.name.toLowerCase() === topicName.toLowerCase());
          if (t && t.topic_id !== selectedTopicId) {
              setSelectedTopicId(t.topic_id);
          }
      }
  }, [topics, searchParams, selectedTopicId]);

  // Trigger refresh when sort/search/topic changes
  // We use a debounce for search, immediate for others
  useEffect(() => {
    const timer = setTimeout(() => {
        refresh(selectedTopicId);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, sortBy, selectedTopicId, refresh]);


  const currentTopic = topics.find(t => t.topic_id === selectedTopicId);

  const handleCreatePostClick = () => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
    } else {
      setOpen(true);
    }
  };

  const handleSelectTopic = async (topicId: number | null) => {
    setSelectedTopicId(topicId);
    // refresh callback dependency handles the rest via the effect above
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, md: 4 } }}>
      <Box sx={{ display: "flex", gap: 4 }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: { md: 220, lg: 280 },
            flexShrink: 0,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box sx={{ position: "sticky", top: 80 }}>
            <TopicSidebar
              topics={topics}
              selectedTopicId={selectedTopicId}
              onSelectTopic={handleSelectTopic}
              onCreateTopic={() => setOpenTopic(true)}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 2, px: 2 }}
            >
              © 2026 Threadly
            </Typography>
          </Box>
        </Box>

        {/* Main Feed */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
             {/* Header Controls */}
             <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }} justifyContent="space-between">
              <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -1, background: 'linear-gradient(45deg, #2563eb, #7c3aed)',WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      {currentTopic ? currentTopic.name : "Popular Posts"}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {currentTopic ? ` discussions in ${currentTopic.name}` : "What's happening in the community"}
                  </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                  <TextField 
                      placeholder="Search..." 
                      size="small"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                          startAdornment: (
                              <InputAdornment position="start">
                                  <SearchIcon fontSize="small" color="action" />
                              </InputAdornment>
                          ),
                          sx: { borderRadius: 8, bgcolor: 'background.paper', width: 200 }
                      }}
                  />
                  <ToggleButtonGroup
                      value={sortBy}
                      exclusive
                      onChange={(e, newSort) => { if(newSort) setSortBy(newSort); }}
                      size="small"
                      sx={{ bgcolor: 'background.paper', borderRadius: 8 }}
                  >
                      <ToggleButton value="top" sx={{ px: 2, borderRadius: 8, textTransform: 'none', fontWeight: 600 }}>
                          Top
                      </ToggleButton>
                      <ToggleButton value="new" sx={{ px: 2, borderRadius: 8, textTransform: 'none', fontWeight: 600 }}>
                          New
                      </ToggleButton>
                  </ToggleButtonGroup>
              </Stack>
          </Stack>

          {currentUserId !== null && (
            <CreatePostBar onClick={handleCreatePostClick} />
          )}

          <Stack spacing={3} sx={{ mt: 3 }}>
            {posts.length === 0 ? (
              <Box sx={{ py: 8, textAlign: "center", bgcolor: 'action.hover', borderRadius: 4 }}>
                  <InboxIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    No posts found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Try adjusting your search or create a new post!
                  </Typography>
                  <Button variant="outlined" onClick={handleCreatePostClick}>
                    Create Post
                  </Button>
              </Box>
            ) : (
                <Stack spacing={3} component={motion.div} variants={container} initial="hidden" animate="show">
              <AnimatePresence mode="popLayout">
              {posts.map((p, index) => (
                <motion.div 
                    key={p.post_id} 
                    layout 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                >
                  <Box>
                    <PostCard
                      post={p}
                      myVote={myVotes[p.post_id] ?? 0}
                      onVoted={() => refresh(null)}
                      currentUserId={currentUserId}
                      onDelete={() => refresh(null)}
                    />
                  </Box>
                </motion.div>
              ))}
              </AnimatePresence>
                </Stack>
            )}
          </Stack>
        </Box>

        {/* Right Sidebar */}
        <Box
          sx={{
            width: { lg: 300 },
            flexShrink: 0,
            display: { xs: "none", lg: "block" },
          }}
        >
          <Box sx={{ position: "sticky", top: 80 }}>
            <CommunityInfo onCreate={handleCreatePostClick} />
          </Box>
        </Box>
      </Box>

      <CreatePostDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(newPost) => {
            if (newPost && newPost.post_id && newPost.title && newPost.username) {
                setPosts(prev => [newPost, ...prev]);
            } else {
                refresh(null);
            }
        }}
        defaultTopic={topics.find(t => t.topic_id === selectedTopicId)?.name}
      />
      
      <CreateTopicDialog
        open={openTopic}
        onClose={() => setOpenTopic(false)}
        onCreated={refreshTopics}
      />
    </Container>
  );
}
